import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import {
  Box,
  Button,
  Container,
  IconButton,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";
import { Grid } from "@mui/system";
import { useBackofficeConstant } from "components/context/ConstantProvider";
import {
  SlideSavedCounter,
  SlideSaverProvider,
  SlideToSave,
  useSlideSaver,
} from "components/hooks/SlideSaverProvider";
import useQueryStringState from "components/hooks/useQueryStringState";
import { LabelValueForText } from "components/ui/atoms/LabelValue";
import useCustomDataSource from "components/ui/customs/useCustomDataSource";
import { Fragment, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  flattenInventoryRows,
  paginateInventoryRows,
} from "../modelComponents/inventoryPagination";
import useVisualisationInformation from "../modelComponents/useVisualisationInformation";
import DealSlide1 from "./slides/DealSlide1";
import DealSlide2 from "./slides/DealSlide2";
import FundInventorySlide from "./slides/FundInventorySlide";
import FundSummaryAddonSlide from "./slides/FundSummaryAddonSlide";
import FundSummarySlide from "./slides/FundSummarySlide";

export const FundVisualizationInformationForm = ({
  state,
  setState,
  saveReady,
  fullName,
}) => {
  const valuationPeriods = useBackofficeConstant("operations.ValuationPeriod");
  const { saveSlides } = useSlideSaver();

  return (
    <>
      <Grid container spacing={2} paddingY={2} alignItems="center">
        <TextField
          select
          size="small"
          value={state.valuationPeriodId}
          onChange={(e) =>
            setState({ ...state, valuationPeriodId: e.target.value })
          }
        >
          {valuationPeriods.map((vp) => (
            <MenuItem key={vp.id} value={vp.id}>
              {vp._title}
            </MenuItem>
          ))}
          {!valuationPeriods.length && (
            <MenuItem value="">
              <em>No Valuation Periods Available</em>
            </MenuItem>
          )}
        </TextField>
        <TextField
          select
          size="small"
          value={state.language}
          onChange={(e) => setState({ ...state, language: e.target.value })}
        >
          <MenuItem key={"en"} value={"en"}>
            English
          </MenuItem>
          <MenuItem key={"fr"} value={"fr"}>
            Français
          </MenuItem>
        </TextField>
        <Button
          variant="contained"
          onClick={saveSlides}
          disabled={saveReady}
          sx={{ ml: "auto" }}
        >
          Save
        </Button>
      </Grid>
      <Stack spacing={2} alignItems="center">
        <Box width="100%">
          <LabelValueForText label={fullName} value={<SlideSavedCounter />} />
        </Box>
      </Stack>
    </>
  );
};

const FundHome = ({ page, data: fund }) => {
  const valuationPeriods = useBackofficeConstant("operations.ValuationPeriod");
  const { i18n } = useTranslation("translation");
  const [qsState, setQState] = useQueryStringState({
    valuationPeriodId: valuationPeriods[0].id,
    scaleActive: true,
    language: "en",
  });
  const handleToggleScaleActive = () => {
    setQState({ ...qsState, scaleActive: !qsState.scaleActive });
  };

  const { vizInfo, isSuccess, status } = useVisualisationInformation({
    fundId: fund.id,
    valuationPeriodId: qsState.valuationPeriodId,
  });

  const inventoryPages = useMemo(
    () => (vizInfo ? paginateInventoryRows(flattenInventoryRows(vizInfo)) : []),
    [vizInfo],
  );

  const { useGetAllByIds } = useCustomDataSource(
    "visualization.DealVisualizationInformation",
  );

  const valuationPeriod = valuationPeriods.find(
    (vp) => vp.id === qsState.valuationPeriodId,
  );

  const fullName = `${fund.name} - ${valuationPeriod.date}`;

  const lang_ext = (function () {
    if (qsState.language && qsState.language === "en") {
      return "";
    }
    return " - FR";
  })();

  const {
    data: dealsVisualizationInformation,
    isSuccess: dealsIsSuccess,
    status: dealsStatus,
  } = useGetAllByIds({
    fundId: fund.id,
    valuationPeriodId: qsState.valuationPeriodId,
  });

  useEffect(() => {
    i18n.changeLanguage(qsState.language);
  }, [qsState.language]);

  return (
    <Container>
      <SlideSaverProvider name={fullName}>
        <FundVisualizationInformationForm
          fullName={fullName + lang_ext}
          state={qsState}
          setState={setQState}
          saveReady={!isSuccess || !dealsIsSuccess}
        />
        <Box display={"flex"} width={1}>
          <IconButton
            onClick={handleToggleScaleActive}
            sx={{
              marginLeft: "auto",
              right: "-8px",
            }}
            size="small"
          >
            {qsState.scaleActive ? (
              <FullscreenIcon fontSize="small" />
            ) : (
              <FullscreenExitIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
        {vizInfo && (
          <>
            <SlideToSave name={`fund_summary_viz.png`}>
              <FundSummarySlide
                vizInfo={vizInfo}
                scaleActive={qsState.scaleActive}
                configurationLinks={[]}
              />
            </SlideToSave>
            {inventoryPages.map((rows, pageIndex) => (
              <SlideToSave
                key={pageIndex}
                name={
                  pageIndex === 0
                    ? `fund_inventory_viz.png`
                    : `fund_inventory_viz_${pageIndex + 1}.png`
                }
              >
                <FundInventorySlide
                  vizInfo={vizInfo}
                  rows={rows}
                  scaleActive={qsState.scaleActive}
                  configurationLinks={[]}
                />
              </SlideToSave>
            ))}
            <SlideToSave name={`fund_summary_addon_viz.png`}>
              <FundSummaryAddonSlide
                vizInfo={vizInfo}
                scaleActive={qsState.scaleActive}
                configurationLinks={[]}
              />
            </SlideToSave>
            {dealsVisualizationInformation?.map((dvi) => (
              <Fragment key={dvi.deal.id}>
                <SlideToSave name={`${dvi.deal.slug}_slide_viz_1.png`}>
                  <DealSlide1
                    dealVisualizationInformation={dvi}
                    scaleActive={qsState.scaleActive}
                    configurationLinks={[]}
                  />
                </SlideToSave>
                <SlideToSave name={`${dvi.deal.slug}_slide_viz_2.png`}>
                  <DealSlide2
                    dealVisualizationInformation={dvi}
                    scaleActive={qsState.scaleActive}
                    configurationLinks={[]}
                  />
                </SlideToSave>
              </Fragment>
            ))}
          </>
        )}
      </SlideSaverProvider>
    </Container>
  );
};

export default FundHome;
